/** Smooth line paths shared by the chart renderers. */
import { round } from './svg';

/**
 * Tangents for a monotone cubic (Fritsch–Carlson): the curve never overshoots the data, so it
 * never dips below zero or above a peak.
 */
const monotoneTangents = (points: Array<{ x: number; y: number }>): number[] => {
    const n = points.length;
    const slopes = points
        .slice(0, -1)
        .map((p, i) => (points[i + 1].y - p.y) / (points[i + 1].x - p.x || 1));
    const tangents = points.map((_, i) => {
        if (i === 0) return slopes[0];
        if (i === n - 1) return slopes[n - 2];
        return slopes[i - 1] * slopes[i] <= 0 ? 0 : (slopes[i - 1] + slopes[i]) / 2;
    });
    slopes.forEach((slope, i) => {
        if (slope === 0) {
            tangents[i] = 0;
            tangents[i + 1] = 0;
            return;
        }
        const a = tangents[i] / slope;
        const b = tangents[i + 1] / slope;
        const h = a * a + b * b;
        if (h > 9) {
            const t = 3 / Math.sqrt(h);
            tangents[i] = t * a * slope;
            tangents[i + 1] = t * b * slope;
        }
    });
    return tangents;
};

/** Cubic path through the points; `smoothing` 0 gives straight segments, 1 the full curve. */
export const buildCurve = (points: Array<{ x: number; y: number }>, smoothing: number): string => {
    if (points.length === 0) return '';
    const start = `M${round(points[0].x)} ${round(points[0].y)}`;
    if (points.length === 1) return start;
    const tangents = monotoneTangents(points);
    const segments = points.slice(1).map((p, i) => {
        const prev = points[i];
        const dx = p.x - prev.x;
        const straight = (p.y - prev.y) / (dx || 1);
        const t0 = straight + smoothing * (tangents[i] - straight);
        const t1 = straight + smoothing * (tangents[i + 1] - straight);
        return (
            `C${round(prev.x + dx / 3)} ${round(prev.y + (t0 * dx) / 3)} ` +
            `${round(p.x - dx / 3)} ${round(p.y - (t1 * dx) / 3)} ${round(p.x)} ${round(p.y)}`
        );
    });
    return start + segments.join('');
};
